import { formatNum } from '@intelligence/utils/commonUtils';
import { isEmpty } from '@intelligence/v3/utils/common';
import { Zoom } from '@intelligence/enums/common';
import { mapColor } from '@intelligence/pages/MarketProfile/_config/Const';

import type { EChartsType } from 'echarts';

/**
 * echarts世界地图缩放
 * @param mapChartInstance echarts世界地图实例
 * @param type 缩放类型
 * @param config 配置
 * @param config.min 最小缩放系数 默认为1
 * @param config.max 最大缩放系数 默认为15
 */
export const mapZoom = (mapChartInstance: EChartsType | null, type: Zoom, config?: { min: number; max: number }) => {
  if (!mapChartInstance) return;

  const min = config?.min ?? 1;
  const max = config?.max ?? 15;
  // echarts世界地图实例的当前配置
  const option = mapChartInstance.getOption();
  // @ts-ignore
  if (!option?.series?.[0]) return;

  // @ts-ignore
  let mapZoomNow = option.series[0].zoom;
  if (type === Zoom.ZoomIn) {
    mapZoomNow = Math.min(max, mapZoomNow + 1);
  } else if (type === Zoom.ZoomOut) {
    mapZoomNow = Math.max(min, mapZoomNow - 1);
  }
  // @ts-ignore
  option.series[0].zoom = mapZoomNow;
  mapChartInstance.setOption(option);
};

/**
 * 根据数据动态生成 ECharts visualMap 的 pieces
 * @param {number[]} data - 数据数组
 * @returns {Array<object>} ECharts visualMap pieces
 */
export const getPiecesDefault = (data?: number[]) => {
  if (!Array.isArray(data) || data.length === 0) {
    // 如果没有数据，返回一个默认的或者空的配置
    return [{ min: 0, max: 1, label: '0', color: mapColor[0] }];
  }

  const maxValue = Math.max(...data.filter((item) => !isEmpty(item)));

  // 如果最大值是0，特殊处理
  if (maxValue === 0) {
    return [{ value: 0, label: '0', color: mapColor[0] }];
  }

  // 1. 找到最大值所在的数量级
  const orderOfMagnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));

  // 2. 找到最接近该数量级的 "1, 2, 5" 系列的分割点
  // 比如 maxValue 是 8345, orderOfMagnitude 是 1000。
  // 那么 top 将会是 10000 (1 * 10^4)
  let top = Math.pow(10, Math.ceil(Math.log10(maxValue)));
  if (maxValue / orderOfMagnitude < 5) {
    top = 5 * orderOfMagnitude;
  }
  if (maxValue / orderOfMagnitude < 2) {
    top = 2 * orderOfMagnitude;
  }
  if (maxValue / orderOfMagnitude < 1) {
    top = orderOfMagnitude;
  }

  // 3. 基于计算出的顶部值，生成6个分割点
  const steps = [top, top / 2, top / 5, top / 10, top / 20, top / 50, 0].map((s) => Math.floor(s)); // 取整以避免小数

  // 4. 创建 pieces
  const pieces = [];
  for (let i = 0; i < 6; i++) {
    const min = steps[i + 1];
    const max = steps[i];
    if (min < max) {
      // 避免重复或无效的区间
      pieces.push({
        min,
        max,
        label: `${formatNum(min, false, 0)}`,
        color: mapColor[6 - i],
      });
    }
  }

  // 5. 添加最后一个 "0" 的区间
  pieces.push({
    min: 0,
    max: steps[5] > 0 ? steps[5] : 1, // 保证 max > min
    label: '0',
    color: mapColor[0],
  });

  // 反转数组以符合 ECharts 从小到大的顺序
  return pieces.reverse();
};

/**
 * 组装echarts地图动态配置（跟数据有关）
 * @param params 参数对象
 * @param params.tooltipFormatter tooltip formatter
 * @param params.pieces 地图左侧数据单位分段配置
 * @param params.data 数据
 */
export const getWorldMapDynamicOption = (params: {
  tooltipFormatter?: Function | null;
  pieces?: any[] | null;
  data?: IMapChartData[] | null;
}) => {
  const tooltipFormatter = params?.tooltipFormatter;
  const pieces = params?.pieces;
  const data = params?.data;

  return {
    tooltip: {
      formatter: tooltipFormatter,
    },
    visualMap: [
      {
        pieces,
      },
    ],
    series: [
      {
        data,
      },
    ],
  };
};
